import styled from 'styled-components';
import InfoIcon from 'design-system/src/assets/icons/infoIcon.svg?react';

type InfoBoxVariant = 'light' | 'medium' | 'transparent' | 'surface' | 'success' | 'info';

const getStylesForVariant = (variant: InfoBoxVariant | undefined) => {
  if (variant === 'light') {
    return {
      background: 'var(--grayscale-50)',
      color: 'var(--grayscale-900)',
    };
  }
  if (variant === 'transparent') {
    return {
      background: 'transparent',
      border: '1px solid var(--grayscale-50)',
      color: 'var(--grayscale-600)',
    };
  }
  if (variant === 'surface') {
    return {
      background: 'var(--surface-200)',
      color: 'var(--grayscale-900)',
    };
  }
  if (variant === 'success') {
    return {
      // TODO(tokens): Replace legacy color token --light-green-2 with numbered equivalent once available.
      background: 'var(--light-green-2)',
      color: 'var(--green-800)',
    };
  }
  if (variant === 'info') {
    return {
      background: 'var(--teal-100)',
      color: 'var(--teal-800)',
    };
  }
  return {
    background: 'var(--grayscale-50)',
    color: 'var(--grayscale-900)',
  };
};

const StyledInfoBox = styled.div<{
  $variant?: InfoBoxVariant;
  $noBottomMargin?: boolean;
  $noTopMargin?: boolean;
  $width?: string;
}>`
  display: flex;
  padding: 8px;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  border-radius: 4px;
  ${({ $variant }) => getStylesForVariant($variant)}
  margin-bottom: ${({ $noBottomMargin }) => ($noBottomMargin ? '0' : '24px')};
  margin-top: ${({ $noTopMargin }) => ($noTopMargin ? '0' : '24px')};
  ${({ $width }) => $width && `width: ${$width};`}
`;

const StyledInfoIcon = styled(InfoIcon)`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: inherit;
  // push it down to align with the first line of text
  margin-top: 3px;
`;

export type InfoBoxProps = {
  children: React.ReactNode;
  className?: string;
  includeIcon?: boolean;
  variant?: InfoBoxVariant;
  noBottomMargin?: boolean;
  noTopMargin?: boolean;
  width?: string;
};

export const InfoBox = ({
  children,
  className,
  includeIcon = true,
  variant = 'light',
  noTopMargin = false,
  noBottomMargin = false,
  width,
}: InfoBoxProps) => {
  return (
    <StyledInfoBox
      className={className}
      $variant={variant}
      $noBottomMargin={noBottomMargin}
      $noTopMargin={noTopMargin}
      $width={width}
    >
      {includeIcon && <StyledInfoIcon />}
      {/* wrap the children in a div to prevent the gap on StyledInfoBox from affecting the children */}
      <div className="w-full">{children}</div>
    </StyledInfoBox>
  );
};
